import type { IParameter } from '../../create-api-context';
import type { GraphQLOperationType } from 'features/chat/agent-chat-module/services/gpt-chat.service';

export type { GraphQLOperationType };

export interface GraphQLVariable {
  name: string;
  /** Raw GraphQL type, e.g. `ID!`, `[Int!]`, `CreateUserInput!`. */
  rawType: string;
  required: boolean;
}

const SCALAR_TYPE_MAP: Record<string, string> = {
  id: 'string',
  string: 'string',
  int: 'integer',
  float: 'number',
  boolean: 'boolean',
};

/** Operation kind from the leading keyword; shorthand `{ ... }` and anything non-mutation is a query. */
export const getGraphQLOperationType = (query: string): GraphQLOperationType => {
  const match = /\b(query|mutation|subscription)\b/.exec(query || '');
  return match?.[1] === 'mutation' ? 'mutation' : 'query';
};

/**
 * Extracts `$variable: Type` definitions from an operation signature. Variable
 * usages (`field: $var`) are ignored since only definitions are `$name: Type`.
 */
export const parseGraphQLVariables = (query: string): GraphQLVariable[] => {
  const variables: GraphQLVariable[] = [];
  const seen = new Set<string>();
  const re = /\$(\w+)\s*:\s*([^,)=]+)/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(query || '')) !== null) {
    const name = match[1];
    if (seen.has(name)) continue;
    seen.add(name);
    const rawType = match[2].trim();
    variables.push({ name, rawType, required: rawType.endsWith('!') });
  }

  return variables;
};

/** Maps a GraphQL type to an Input-Parameter type; list → array, scalars mapped, input/enum → object. */
export const graphqlTypeToParamType = (rawType: string): string => {
  const trimmed = (rawType || '').trim();
  if (trimmed.startsWith('[')) return 'array';
  const base = trimmed.replace(/[[\]!\s]/g, '').toLowerCase();
  return SCALAR_TYPE_MAP[base] ?? 'object';
};

/** Builds the Input-Parameters schema (keyed by variable name) from parsed variables. */
export const buildGraphQLInputParameters = (
  variables: GraphQLVariable[]
): Record<string, IParameter> => {
  const result: Record<string, IParameter> = {};

  variables.forEach((variable) => {
    result[variable.name] = {
      Name: variable.name,
      Type: graphqlTypeToParamType(variable.rawType),
      Description: '',
      Required: variable.required,
      Location: 'body',
      DefaultValue: '',
    };
  });

  return result;
};

/**
 * Prefills Input Parameters from a query's variables, keeping any existing
 * definitions (user edits) for variables already present.
 */
export const mergeGraphQLInputParameters = (
  query: string,
  existing: Record<string, IParameter> = {}
): Record<string, IParameter> => {
  const parsed = buildGraphQLInputParameters(parseGraphQLVariables(query));
  const merged: Record<string, IParameter> = {};

  Object.entries(parsed).forEach(([name, param]) => {
    merged[name] = existing[name] ?? param;
  });

  return merged;
};
